import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Check,
    ChevronDown,
    ChevronUp,
    Clock,
    Copy,
    RefreshCw,
    ShieldCheck,
    Sparkles,
    TrendingDown,
    TrendingUp,
    X,
} from 'lucide-react';
import {
    fetchGeoOptimizerDashboard,
    applyOptimizerProposal,
    rejectOptimizerProposal,
    OptimizerProposal,
} from '../../services/geoOptimizerApi';
import {
    Rolling72hSnapshot,
    SnoozeEntry,
    loadSnoozeMap,
    saveSnoozeEntry,
} from '../layout/NotificationCenter';

const fmtDkk = (n?: number | null) =>
    `${Math.round(Number(n || 0)).toLocaleString('da-DK')} kr.`;

const fmtPct = (ratio?: number | null) =>
    `${(Number(ratio || 0) * 100).toFixed(1)}%`;

export const Rolling72hGovernanceCard: React.FC = () => {
    const [showAdTable, setShowAdTable] = useState(false);
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const [snoozeMenuId, setSnoozeMenuId] = useState<number | null>(null);
    const [snoozeMap, setSnoozeMap] = useState<Record<string, SnoozeEntry>>(() => loadSnoozeMap());
    const [feedback, setFeedback] = useState<string | null>(null);
    const queryClient = useQueryClient();

    useEffect(() => {
        const syncSnooze = () => setSnoozeMap(loadSnoozeMap());
        window.addEventListener('afima-72h-snooze-changed', syncSnooze);
        const timer = setInterval(syncSnooze, 30_000);
        return () => {
            window.removeEventListener('afima-72h-snooze-changed', syncSnooze);
            clearInterval(timer);
        };
    }, []);

    const { data, isFetching, refetch } = useQuery({
        queryKey: ['se-rolling-72h-notifications'],
        queryFn: () => fetchGeoOptimizerDashboard('se', 3),
        staleTime: 30_000,
    });

    const latestRun = data?.latest_run;
    const snap = (((data?.portfolio as any)?.rolling_72h) ||
        ((latestRun as any)?.portfolio_snapshot?.rolling_72h)) as Rolling72hSnapshot | undefined;
    const proposals: OptimizerProposal[] = data?.proposals || [];
    const pendingProposals = proposals.filter(
        (p) => p.status === 'pending' && !snoozeMap[String(p.id)]
    );
    const historyProposals = proposals
        .filter((p) => p.status === 'applied' || p.status === 'rejected' || Boolean(snoozeMap[String(p.id)]))
        .slice(0, 4);

    const notify = (msg: string) => {
        setFeedback(msg);
        setTimeout(() => setFeedback(null), 4500);
    };

    const approveMut = useMutation({
        mutationFn: (id: number) => applyOptimizerProposal(id),
        onSuccess: (_res, id) => {
            const p = proposals.find((x) => x.id === id);
            const payload = (p?.action_payload || {}) as Record<string, any>;
            if (payload.action === 'update_live_budget') {
                notify(`✓ Dagsbudget opdateret live på Meta (og kl. 07:30 morgen-regel) til ${fmtDkk(payload.daily_budget)}/dag!`);
            } else if (payload.notification_kind === 'creative_sanitizing') {
                notify(`✓ Creative sanitizing markeret som gjort for "${payload.ad_name || p?.title}"!`);
            } else {
                notify(`✓ Fastholdelse af dagsbudget (${fmtDkk(payload.daily_budget)}/dag) bekræftet!`);
            }
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
        onError: (err: any) => {
            notify(`⚠️ Kunne ikke udføre handling: ${err?.message || 'Ukendt fejl'}`);
        },
    });

    const rejectMut = useMutation({
        mutationFn: ({ id }: { id: number; reason?: string }) => rejectOptimizerProposal(id),
        onSuccess: () => {
            notify('Forslag afvist — nuværende budget fastholdes.');
            queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
        },
    });

    const handleSnooze = (p: OptimizerProposal, hours: number, label: string, persistBackend = false) => {
        const nextMap = saveSnoozeEntry(p.id, hours, label);
        setSnoozeMap(nextMap);
        setSnoozeMenuId(null);
        notify(`⏰ Udskudt (${label}): "${p.title}"`);
        if (persistBackend) {
            rejectOptimizerProposal(p.id)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['se-rolling-72h-notifications'] });
                })
                .catch(() => {});
        }
    };

    const copyText = (key: string, text: string) => {
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        notify('📋 Analyse-prompt kopieret! Indsæt den i chatten til Antigravity.');
        setTimeout(() => setCopiedKey(null), 3000);
    };

    if (!snap) {
        return null;
    }

    const zone = snap.zone || 'yellow';
    const zoneBanner =
        zone === 'green'
            ? 'border-emerald-300 bg-emerald-50/70 text-emerald-950'
            : zone === 'red'
            ? 'border-rose-300 bg-rose-50/70 text-rose-950'
            : 'border-amber-300 bg-amber-50/60 text-amber-950';

    const ads = snap.ads_breakdown || [];

    return (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Header Bar */}
            <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3 ${zoneBanner}`}>
                <div className="flex items-center gap-2.5">
                    <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-white/90 border border-black/10 shadow-xs">
                        72t Auto-Styring · Sverige
                    </span>
                    <div>
                        <h2 className="text-sm font-bold">{snap.zone_label}</h2>
                        <p className="text-xs opacity-80">
                            Kører automatisk hver dag kl. 17:00 CET (notifikation i topbaren + e-mail) · Morgen-regel kl. 07:30 ({fmtDkk(snap.current_daytime_budget_dkk)}/dag) · Nat-regel kl. 23:00 (1.200 kr./dag)
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {snap.evaluated_at && (
                        <span className="text-xs opacity-75 flex items-center gap-1">
                            <Clock size={12} />
                            Evalueret{' '}
                            {new Date(snap.evaluated_at).toLocaleString('da-DK', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1 text-xs font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
                    >
                        <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
                        Opdater
                    </button>
                </div>
            </div>

            {feedback && (
                <div className="px-4 py-2 bg-emerald-900 text-white text-xs font-medium flex items-center justify-between">
                    <span>{feedback}</span>
                    <button onClick={() => setFeedback(null)} className="text-emerald-200 hover:text-white">
                        ✕
                    </button>
                </div>
            )}

            {/* 4 Rolling 72h KPI Tiles */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-gray-50/50 border-b border-gray-100">
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs font-medium text-gray-500">72-Timers Instant ROAS</div>
                    <div className="mt-1 flex items-baseline gap-2">
                        <span
                            className={`text-xl font-bold ${
                                zone === 'green'
                                    ? 'text-emerald-700'
                                    : zone === 'red'
                                    ? 'text-rose-700'
                                    : 'text-amber-700'
                            }`}
                        >
                            {fmtPct(snap.instant_roas_72h)}
                        </span>
                        <span className="text-xs font-medium text-gray-500">
                            (~{fmtPct(snap.projected_ltv_roas_72h)} Total LTV)
                        </span>
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                        Grøn: &ge;85% (+25%) · Gul: 70–84% (Hold) · Rød: &lt;65% (-20%)
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs font-medium text-gray-500">72t Spend &rarr; Direkte Kommission</div>
                    <div className="mt-1 text-lg font-bold text-gray-900">
                        {fmtDkk(snap.spend_72h_dkk)} &rarr; {fmtDkk(snap.rev_72h_dkk)}
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                        {snap.purchases_72h || 0} godkendte salg (Salus + Lendo SE)
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs font-medium text-gray-500">Approved by Salus (72t)</div>
                    <div className="mt-1 text-lg font-bold text-gray-900">
                        {snap.salus_approved_72h || 0} stk · {fmtDkk(snap.cpa_salus_72h_dkk)}/stk
                    </div>
                    <div className="mt-0.5 text-[11px] font-medium text-amber-700">
                        +{snap.today_salus_pending || 0} friske Salus-ansøgninger i 4,5t bank-kø i dag
                    </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs font-medium text-gray-500">I dag ({snap.window?.to})</div>
                    <div className="mt-1 text-lg font-bold text-gray-900">
                        {fmtDkk(snap.spend_today_dkk)} &rarr; {fmtDkk(snap.rev_today_dkk)} ({fmtPct(snap.instant_roas_today)})
                    </div>
                    <div className="mt-0.5 text-[11px] text-gray-500">
                        {snap.purchases_today || 0} køb i dag · Aktuelt dagsbudget: {fmtDkk(snap.current_daytime_budget_dkk)}
                    </div>
                </div>
            </div>

            {/* Actionable Proposals & Creative Sanitizing Tasks */}
            <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
                        Afventer din godkendelse / handling ({pendingProposals.length})
                    </h3>
                    <button
                        type="button"
                        onClick={() => setShowAdTable((v) => !v)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 hover:text-emerald-950"
                    >
                        <span>{showAdTable ? 'Skjul 72t annonce-tabel' : `Vis 72t Creative Sanitizing tabel (${ads.length} annoncer)`}</span>
                        {showAdTable ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                </div>

                {pendingProposals.length === 0 ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-xs text-emerald-900 flex items-center justify-between">
                        <span>✓ Alle dagens 72-timers budget- og creative sanitizing-punkter er gennemgået og markeret som gjort!</span>
                        {historyProposals.length > 0 && (
                            <span className="text-[11px] text-emerald-700 font-medium">
                                Senest godkendt: {historyProposals[0].title}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {pendingProposals.map((p) => {
                            const payload = (p.action_payload || {}) as Record<string, any>;
                            const isBudget = p.proposal_type === 'budget_shift';
                            const isLiveBudgetChange = payload.action === 'update_live_budget';
                            const pZone = payload.zone || zone;

                            return (
                                <div
                                    key={p.id}
                                    className="rounded-xl border border-gray-200 bg-gray-50/60 p-3.5 flex flex-col justify-between gap-3"
                                >
                                    <div>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-1.5">
                                                {isBudget ? (
                                                    pZone === 'green' ? (
                                                        <TrendingUp size={16} className="text-emerald-600 shrink-0" />
                                                    ) : pZone === 'red' ? (
                                                        <TrendingDown size={16} className="text-rose-600 shrink-0" />
                                                    ) : (
                                                        <ShieldCheck size={16} className="text-amber-600 shrink-0" />
                                                    )
                                                ) : (
                                                    <Sparkles size={16} className="text-sky-600 shrink-0" />
                                                )}
                                                <span className="text-sm font-semibold text-gray-900">{p.title}</span>
                                            </div>
                                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium shrink-0">
                                                {isBudget ? 'Budget-styring' : 'Creative Sanitizing'}
                                            </span>
                                        </div>
                                        <p className="mt-1.5 text-xs text-gray-600 leading-relaxed">{p.rationale}</p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 pt-1">
                                        {isBudget && isLiveBudgetChange && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => approveMut.mutate(p.id)}
                                                    disabled={approveMut.isPending}
                                                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-xs ${
                                                        pZone === 'green'
                                                            ? 'bg-emerald-700 hover:bg-emerald-800'
                                                            : 'bg-rose-700 hover:bg-rose-800'
                                                    }`}
                                                >
                                                    <Check size={14} />
                                                    {pZone === 'green'
                                                        ? `Godkend opjustering (${fmtDkk(payload.daily_budget)}/dag)`
                                                        : `Godkend nedjustering (${fmtDkk(payload.daily_budget)}/dag)`}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        rejectMut.mutate({
                                                            id: p.id,
                                                            reason: 'Fastholder nuværende dagsbudget',
                                                        })
                                                    }
                                                    disabled={rejectMut.isPending}
                                                    className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    Afvis (Behold {fmtDkk(payload.previous_daily_budget)})
                                                </button>
                                            </>
                                        )}

                                        {isBudget && !isLiveBudgetChange && (
                                            <button
                                                type="button"
                                                onClick={() => approveMut.mutate(p.id)}
                                                disabled={approveMut.isPending}
                                                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white shadow-xs"
                                            >
                                                <Check size={14} />
                                                Bekræft fastholdelse ({fmtDkk(payload.daily_budget)}/dag)
                                            </button>
                                        )}

                                        {!isBudget && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        copyText(
                                                            `prop-${p.id}`,
                                                            payload.antigravity_prompt ||
                                                                `Analyser venligst "${p.target_object_name}": ${p.rationale}`
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-900 hover:bg-sky-100"
                                                >
                                                    <Copy size={13} />
                                                    {copiedKey === `prop-${p.id}`
                                                        ? 'Kopieret!'
                                                        : 'Kopiér analyse-prompt til Antigravity'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => approveMut.mutate(p.id)}
                                                    disabled={approveMut.isPending}
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 text-xs font-semibold text-white shadow-xs"
                                                >
                                                    <Check size={14} />
                                                    Marker som gjort
                                                </button>
                                            </>
                                        )}

                                        {/* Udskyd (Snooze) Button & Quick Options */}
                                        {snoozeMenuId === p.id ? (
                                            <div className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50/80 px-2.5 py-1">
                                                <span className="text-[11px] font-medium text-amber-900">Udskyd:</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSnooze(p, 3, '3 timer', false)}
                                                    className="rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200 hover:bg-amber-100"
                                                >
                                                    3 timer
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSnooze(p, 24, 'Til i morgen', true)}
                                                    className="rounded bg-white px-2 py-0.5 text-[11px] font-semibold text-gray-800 border border-gray-300 hover:bg-gray-100"
                                                >
                                                    Til i morgen
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setSnoozeMenuId(null)}
                                                    className="p-0.5 text-gray-500 hover:text-gray-800"
                                                    title="Annuller"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => setSnoozeMenuId(p.id)}
                                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                title="Udskyd dette punkt (f.eks. mens vi afventer bank-svar eller mere data)"
                                            >
                                                <Clock size={13} className="text-amber-600" />
                                                Udskyd
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Collapsible 72h Per-Ad Creative Sanitizing Table */}
                {showAdTable && ads.length > 0 && (
                    <div className="mt-3 rounded-lg border border-gray-200 overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold">Annonce (72t rullende)</th>
                                    <th className="px-3 py-2 text-right font-semibold">72t Spend</th>
                                    <th className="px-3 py-2 text-right font-semibold">Link-klik (CTR)</th>
                                    <th className="px-3 py-2 text-right font-semibold">Freq</th>
                                    <th className="px-3 py-2 text-right font-semibold">Approved by Salus</th>
                                    <th className="px-3 py-2 text-right font-semibold">72t Køb (ROAS)</th>
                                    <th className="px-3 py-2 text-right font-semibold">Antigravity Analyse</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 tabular-nums">
                                {ads.map((ad) => {
                                    const prompt = `Analyser creative performance og sanitizing for annoncen "${ad.ad_name}" (ad_id: ${ad.ad_id}) i SL-SE Kvalificerad tratt v2: 72t spend ${Math.round(ad.spend_72h_dkk)} DKK, ${ad.link_clicks_72h} link-klik (${ad.link_ctr_pct}% CTR, freq ${ad.frequency_72h}), ${ad.salus_approved_72h} Approved by Salus (${Math.round(ad.cpa_salus_dkk)} DKK/stk), ${ad.purchases_72h} køb (${fmtPct(ad.instant_roas_72h)} ROAS).`;
                                    return (
                                        <tr key={ad.ad_id} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 font-medium text-gray-900">{ad.ad_name}</td>
                                            <td className="px-3 py-2 text-right text-gray-900">{fmtDkk(ad.spend_72h_dkk)}</td>
                                            <td className="px-3 py-2 text-right text-gray-700">
                                                {ad.link_clicks_72h} ({ad.link_ctr_pct.toFixed(2)}%)
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">{ad.frequency_72h.toFixed(2)}</td>
                                            <td className="px-3 py-2 text-right text-gray-900">
                                                {ad.salus_approved_72h} stk ({fmtDkk(ad.cpa_salus_dkk)})
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                                                {ad.purchases_72h} ({fmtPct(ad.instant_roas_72h)})
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => copyText(`ad-${ad.ad_id}`, prompt)}
                                                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                    <Copy size={11} />
                                                    {copiedKey === `ad-${ad.ad_id}` ? 'Kopieret!' : 'Spørg Antigravity'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
